import React, { useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { usePublicSite, useSettings } from "@/lib/utils-app";
import { COPYRIGHT_OWNER } from "@/lib/gymSiteDefaults";
import { Menu, X, Instagram, Facebook, Twitter, Youtube, ShieldCheck } from "lucide-react";

const links = [
  { to: "/", label: "Inicio" },
  { to: "/paquetes", label: "Paquetes" },
  { to: "/productos", label: "Productos" },
  { to: "/eventos", label: "Eventos" },
  { to: "/contacto", label: "Contacto" },
];

export default function PublicLayout() {
  const site = usePublicSite();
  useSettings();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <div className="min-h-screen flex flex-col" data-testid="public-layout">
      <header className="sticky top-0 z-50 glass-strong border-b border-white/10" data-testid="public-navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            {site.logo ? (
              <img src={site.logo} alt="logo" className="h-9 w-9 object-cover rounded-sm" />
            ) : (
              <div className="h-9 w-9 grid place-items-center bg-[#E63946] text-black font-display text-xl rounded-sm">
                {(site.gym_name || "B").charAt(0)}
              </div>
            )}
            <span className="font-display text-2xl tracking-wider text-white">
              {site.gym_name || "BOXING GYM"}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-display text-lg tracking-wide uppercase transition-colors ${
                    isActive ? "text-[#E63946]" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/mi/login" className="btn-outline text-sm" data-testid="nav-user-login-btn">
              {site.nav_login_label}
            </Link>
            <Link to="/paquetes" className="btn-primary text-sm" data-testid="nav-join-btn">
              {site.nav_join_label}
            </Link>
          </nav>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-black/95 px-4 py-4 space-y-3" data-testid="mobile-menu">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block font-display text-lg ${isActive ? "text-[#E63946]" : "text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/mi/login" className="btn-outline text-sm w-full justify-center" onClick={() => setOpen(false)}>
              {site.nav_login_label}
            </Link>
            <Link to="/paquetes" className="btn-primary text-sm w-full justify-center" onClick={() => setOpen(false)}>
              {site.nav_join_label}
            </Link>
          </div>
        )}
      </header>

      <main key={loc.pathname} className="flex-1 anim-fade-up">
        <Outlet />
      </main>

      <footer className="border-t border-white/10 bg-black/60 mt-16" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="font-display text-3xl text-white">{site.gym_name}</div>
            {site.footer_blurb && <p className="mt-2 text-sm text-white/60">{site.footer_blurb}</p>}
            <p className="mt-2 text-sm text-white/60">{site.address}</p>
            <p className="mt-1 text-sm text-white/60">{site.phone}</p>
            <p className="mt-1 text-sm text-white/60">{site.contact_email}</p>
          </div>
          <div>
            <div className="font-display text-xl text-[#FFD700]">Navegación</div>
            <ul className="mt-2 space-y-1 text-sm">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/60 hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-display text-xl text-[#FFD700]">Síguenos</div>
            <div className="mt-2 flex gap-3">
              {site.facebook && <a href={site.facebook} target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#E63946]"><Facebook /></a>}
              {site.instagram && <a href={site.instagram} target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#E63946]"><Instagram /></a>}
              {site.twitter && <a href={site.twitter} target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#E63946]"><Twitter /></a>}
              {site.youtube && <a href={site.youtube} target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#E63946]"><Youtube /></a>}
            </div>
            <Link to="/admin/login" className="inline-flex items-center gap-2 mt-6 text-sm text-white/60 hover:text-[#FFD700] border border-white/15 hover:border-[#FFD700]/50 rounded-sm px-3 py-2 transition-all" data-testid="footer-admin-link">
              <ShieldCheck size={14} /> Acceso administrador
            </Link>
          </div>
        </div>
        <div className="border-t border-theme-secondary/10 py-4 text-center text-xs text-white/40">
          © {new Date().getFullYear()} {COPYRIGHT_OWNER}. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  );
}
